/**
 * Dynamic Content stage gate (WI 34928, Task 34976): POST api/DynamicResponse names the gated controls it refused in
 * `rejectedControls`. A partial refusal is a 200 (the other answers saved); a save whose only changes were refused is a
 * 403 whose body carries `writeRefused: true`.
 */

/** The refused control names in a save response body (camelCase or PascalCase), trimmed; [] when there are none. */
export function rejectedControlNames(body: unknown): string[] {
  if (!body || typeof body !== 'object') {
    return [];
  }
  const record = body as Record<string, unknown>;
  const names = record['rejectedControls'] ?? record['RejectedControls'];
  if (!Array.isArray(names)) {
    return [];
  }
  return names
    .filter((n): n is string => typeof n === 'string')
    .map((n) => n.trim())
    .filter((n) => n.length > 0);
}

/** True when a save response body says nothing was written (writeRefused, camelCase or PascalCase). */
export function isWriteRefusedBody(body: unknown): boolean {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const record = body as Record<string, unknown>;
  return (record['writeRefused'] ?? record['WriteRefused']) === true;
}

/** True only for the gate's own refusal: a 403 whose body says writeRefused. Any other error keeps its normal handling. */
export function isWriteRefusal(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const { status, error: body } = error as {
    status?: unknown;
    error?: unknown;
  };
  return status === 403 && isWriteRefusedBody(body);
}

/** The message a user sees for refused controls, each named once. */
export function writeRefusedMessage(names: string[]): string {
  return `Not saved: ${[...new Set(names)].join(', ')}. You do not have permission to change it.`;
}
